import type { ErrorEvent, EventHint } from '@sentry/react';

/**
 * Patterns for errors that should be ignored
 * These are high-volume, low-value errors that create noise
 */
const IGNORED_ERROR_PATTERNS: RegExp[] = [
  // Validation errors (expected behavior, high volume)
  /ValidationError/i,
  /Validation failed/i,

  // Network errors (transient, user's connection issues)
  /Network Error/i,
  /Failed to fetch/i,
  /Load failed/i,
  /NetworkError/i,
  /net::ERR_/i,

  // Chunk loading errors (often caused by deployments while user has old version)
  /ChunkLoadError/i,
  /Loading chunk/i,
  /Loading CSS chunk/i,

  // Auth errors (expected behavior - user not logged in, token expired)
  /401/,
  /Unauthorized/i,
  /403/,
  /Forbidden/i,

  // User cancellation (expected behavior)
  /AbortError/i,
  /CanceledError/i,
  /Request aborted/i,
  /The operation was aborted/i,

  // Browser extension interference
  /ResizeObserver loop/i,
  /ResizeObserver loop limit exceeded/i,

  // Third-party script errors
  /Script error/i,
  /cross-origin/i,

  // CORS errors (usually misconfiguration, not actionable in error tracking)
  /CORS/i,
  /Cross-Origin/i,
  /Access-Control-Allow-Origin/i,

  // Timeout errors (transient)
  /timeout/i,
  /ETIMEDOUT/i,
  /ECONNABORTED/i,
];

/**
 * URL patterns to ignore (third-party scripts)
 */
const IGNORED_URL_PATTERNS: RegExp[] = [
  // Browser extensions
  /extensions\//i,
  /^chrome:\/\//i,
  /^chrome-extension:\/\//i,
  /^moz-extension:\/\//i,

  // Third-party scripts that we can't fix
  /googletagmanager/i,
  /google-analytics/i,
  /facebook\.net/i,
  /connect\.facebook/i,
  /hotjar/i,
  /intercom/i,
];

/**
 * Check if an error message matches any ignored pattern
 */
function matchesIgnoredPattern(message: string | undefined): boolean {
  if (!message) return false;
  return IGNORED_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

/**
 * Check if a URL matches any ignored pattern
 */
function matchesIgnoredUrl(url: string | undefined): boolean {
  if (!url) return false;
  return IGNORED_URL_PATTERNS.some((pattern) => pattern.test(url));
}

/**
 * Extract error message from Sentry event
 */
function getErrorMessage(event: ErrorEvent): string | undefined {
  // Try to get message from exception
  const exceptionValue = event.exception?.values?.[0]?.value;
  if (exceptionValue) return exceptionValue;

  // Fall back to event message
  return event.message;
}

/**
 * Extract URL from event
 */
function getEventUrl(event: ErrorEvent): string | undefined {
  // Try to get from request
  if (event.request?.url) return event.request.url;

  // Try to get from exception stacktrace
  const frames = event.exception?.values?.[0]?.stacktrace?.frames;
  if (frames && frames.length > 0) {
    return frames[frames.length - 1]?.filename;
  }

  return undefined;
}

/**
 * Filter function for Sentry beforeSend
 * Returns null to drop the event, or the event to send it
 */
export function filterError(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
  const message = getErrorMessage(event);
  const url = getEventUrl(event);

  // Check if error message matches ignored patterns
  if (matchesIgnoredPattern(message)) {
    return null;
  }

  // Check original error as well
  const originalError = hint.originalException;
  if (originalError instanceof Error && matchesIgnoredPattern(originalError.message)) {
    return null;
  }

  // Check URL patterns
  if (matchesIgnoredUrl(url)) {
    return null;
  }

  return event;
}

/**
 * Patterns that should be ignored at init time (via ignoreErrors option)
 * These are string patterns for exact matching
 */
export const INIT_IGNORE_ERRORS: (string | RegExp)[] = [
  // ResizeObserver - very common, not actionable
  'ResizeObserver loop limit exceeded',
  'ResizeObserver loop completed with undelivered notifications',

  // Non-Error promise rejections
  'Non-Error promise rejection captured',

  // Navigation errors (user navigating away)
  'Navigation cancelled',
  'Navigating to current location',
];
